import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * - `block` a neutral placeholder block for text and numbers that are still loading
 * - `hatch` the diagonal hatch the design uses wherever an image belongs: photos,
 *   logos, the route map, avatars. This is the one place a gradient is allowed.
 */
export type SkeletonVariant = 'block' | 'hatch';

export type SkeletonProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    variant?: SkeletonVariant;
    width?: number | string;
    height?: number | string;
    radius?: number;
    /** Lighter hatch band for the route map and the logo dropzone. */
    light?: boolean;
    /** 1px `--placeholder-border` around the hatch. */
    bordered?: boolean;
    /**
     * Off by default: only the sync indicator animates in this design. Turn it on
     * for a deferred prop that genuinely takes a moment.
     */
    pulse?: boolean;
    /** Mono caption chip in the bottom-left ("foto dakvlak achterzijde"). */
    caption?: ReactNode;
    /** Announced while loading. Leave empty for purely decorative placeholders. */
    label?: string;
};

export function Skeleton({
    variant = 'block',
    width,
    height,
    radius = 7,
    light = false,
    bordered = false,
    pulse = false,
    caption,
    label,
    className,
    style,
    ...rest
}: SkeletonProps) {
    return (
        <div
            role={label ? 'status' : undefined}
            aria-label={label}
            aria-hidden={label ? undefined : true}
            aria-busy={label ? true : undefined}
            className={cn(
                'flex items-end overflow-hidden',
                variant === 'hatch'
                    ? light
                        ? 'bg-[image:var(--placeholder-hatch-light)]'
                        : 'bg-[image:var(--placeholder-hatch)]'
                    : 'bg-[var(--border-subtle)]',
                bordered && 'border border-[var(--placeholder-border)]',
                pulse && 'animate-[skpulse_2s_infinite]',
                caption ? 'p-[5px]' : null,
                className,
            )}
            style={{
                width: typeof width === 'number' ? `${width}px` : width,
                height:
                    typeof height === 'number'
                        ? `${height}px`
                        : (height ?? (variant === 'block' ? '12px' : undefined)),
                borderRadius: `${radius}px`,
                ...style,
            }}
            {...rest}
        >
            {caption ? (
                <span className="rounded-[3px] bg-[var(--placeholder-caption-bg)] px-1 py-[2px] font-[family-name:var(--font-mono)] text-[9px] text-[var(--text-secondary)]">
                    {caption}
                </span>
            ) : null}
        </div>
    );
}
